"use client";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { ColumnsBlock as ColumnsBlockType } from "@/lib/utils/lomdaV3/types";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import NestedBlocks from "./nestedBlocks";
import { AlignedColumn } from "./columnAlign";
import ResizableColumns from "./resizableColumns";

/**
 * A row of columns; each column is a nested page (its own blocks). Widths are
 * stored as proportions (% of the row) on each column; absent = equal share.
 */
export default function ColumnsBlock({ block }: { block: ColumnsBlockType }) {
    const editable = useBlockEditable();
    const isMobile = useIsMobile();
    const setActiveSlot = useLomdaV3Editor((s) => s.setActiveSlot);
    const { columns } = block.data;
    const activate = (slotId: string) => () =>
        setActiveSlot({ containerId: block.id, slotId });

    const equal = 100 / columns.length;

    // Resizable panels are inherently horizontal, so on a phone the columns
    // would squish side-by-side. Below the md breakpoint we stack them instead.
    if (isMobile) {
        return (
            <div className="flex flex-col gap-4">
                {columns.map((col) => (
                    <div
                        key={col.id}
                        onPointerDownCapture={editable ? activate(col.id) : undefined}
                        className={cn(
                            "min-w-0",
                            editable &&
                                "rounded-xl border border-dashed border-border/70 p-2"
                        )}
                    >
                        <NestedBlocks
                            containerId={block.id}
                            slotId={col.id}
                            blocks={col.blocks}
                            editable={editable}
                        />
                    </div>
                ))}
            </div>
        );
    }

    // Preview / read-only: static columns — no resizable panels, so there are no
    // drag handles or resize cursor. Stored widths apply as flex proportions.
    if (!editable) {
        return (
            <div className="flex w-full items-stretch gap-5">
                {columns.map((col) => (
                    <div
                        key={col.id}
                        className="min-w-0"
                        style={{
                            flexGrow: col.width ?? equal,
                            flexShrink: 1,
                            flexBasis: 0,
                        }}
                    >
                        <AlignedColumn align={col.verticalAlign}>
                            <NestedBlocks
                                containerId={block.id}
                                slotId={col.id}
                                blocks={col.blocks}
                                editable={false}
                            />
                        </AlignedColumn>
                    </div>
                ))}
            </div>
        );
    }

    return <ResizableColumns block={block} />;
}
